import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import {
  CheckmarkCircleRegular,
  ChevronDown20Regular,
  MailRegular,
} from "@fluentui/react-icons";

import { IconButton } from "@/components/ui/icon-button";

import { VisitorTagField } from "@/components/feature/VisitorTagField";
import { Checkbox as UiCheckbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useVisitorTagField } from "@/lib/useVisitorTagField";
import { isTagSelectionMissing } from "@/lib/visitorTagField";
import {
  CHANNEL_APPOINTMENT,
  SYSTEM_COMPANY,
  SYSTEM_EMAIL,
  SYSTEM_NAME,
  isCheckedValue,
  isRequiredFor,
  isSameField,
  visibleFieldsFor,
} from "@/lib/visitorAdditionalFields";
import { buildVisitorTagOptions } from "@/lib/visitorTagOptions";
import { checkTagDuration } from "@/lib/visitorTagDuration";
import { buildLanguageOptions, defaultLanguage } from "@/lib/visitorLanguages";
import { LanguageSwitch } from "@/components/feature/LanguageSwitch";
import { applyTagDoorPermissions } from "@/lib/visitorTagDoorPermissions";
import {
  DoorPermissionFields,
  useDoorPermissions,
} from "@/components/feature/doorPermissions";

import Checkbox from "./Checkbox";
import Dropdown from "./Dropdown";
import Input from "./Input";
import Toggle from "./Toggle";
import VisitorInput from "./VisitorInput";

const convertDropDownValues = (data) => {
  let values = [];
  data.forEach((item) => values.push({ label: item, value: item }));
  return values;
};

const Visitor = (props, ref) => {
  const [isChecked, setIsChecked] = useState(props.initiallyChecked || false);
  const [name, setName] = useState(props.initialName !== props.initialEmail ? props.initialName : "");
  const [email, setEmail] = useState(props.initialEmail || "");
  const [company, setCompany] = useState(props.initialCompany || "");
  const [dynamicFields, setDynamicFields] = useState(
    props.dynamicFields.map((field) => ({
      ...field,
      value: field.inserted_value || "",
      isValid: true,
    })),
  );
  const [selectedTagLabels, setSelectedTagLabels] = useState(props.initialTagLabels || []);
  const [language, setLanguage] = useState(props.initialLanguage || null);
  // Sprachen des Kunden; die Umschaltung erscheint nur ab zwei Einträgen.
  const languageOptions = buildLanguageOptions(
    props.availableLanguages,
    props.locationId,
  );
  // Auswählbare Tags des gewählten Standorts (kundenweite + standortgebundene),
  // inklusive Beschreibung für den Info-Tooltip.
  const tagOptions = buildVisitorTagOptions(props.availableTags, props.locationId);
  const tagField = useVisitorTagField(props.locationId);

  // Zusatzfelder dieser Maske: Kanal „Terminerstellung", und an Tags gebundene
  // Felder erscheinen erst, wenn der passende Tag gewählt ist. Die Reihenfolge
  // ist die aus den Einstellungen.
  // Türberechtigungen: bestehende des Besuchers, ergänzt um die Standardwerte
  // der gewählten Tags. Bearbeitbar, sofern der Standort Dormakaba nutzt und
  // der Benutzer Berechtigungen verwalten darf.
  const { isDormakabaActive, canManagePermissions, optionsByType } =
    useDoorPermissions(props.locationId);
  const showDoorPermissions = isDormakabaActive && canManagePermissions;
  const [doorPermissions, setDoorPermissions] = useState(
    props.initialDoorPermissions || [],
  );

  const handleTagChange = (nextLabels) => {
    setDoorPermissions((current) =>
      applyTagDoorPermissions(current, selectedTagLabels, nextLabels, tagOptions),
    );
    setSelectedTagLabels(nextLabels);
  };

  // Manche Tags begrenzen die Termindauer. Die Zeiten stammen aus dem
  // Outlook-Termin und lassen sich hier nicht ändern — deshalb sagt die
  // Meldung, was zu tun ist.
  const durationIssue = checkTagDuration(
    tagOptions,
    selectedTagLabels,
    props.appointmentStart,
    props.appointmentEnd,
  );

  // Felder dieser Karte in der eingestellten Reihenfolge (Einstellungen →
  // Besucher → Felder), mit der für die gewählten Tags gültigen Bezeichnung und
  // Pflicht. Die E-Mail kommt aus Outlook, steht als Kopf der Karte und ist
  // hier immer Pflicht — sie erscheint nicht noch einmal als Feld, ihre
  // Einstellung gilt im Add-in nicht.
  const visibleConfig = visibleFieldsFor(
    props.fieldConfig || [],
    selectedTagLabels,
    CHANNEL_APPOINTMENT,
  ).filter((field) => field.system_key !== SYSTEM_EMAIL);
  const companyConfig = visibleConfig.find((f) => f.system_key === SYSTEM_COMPANY);
  const companyRequired =
    !!companyConfig && isRequiredFor(companyConfig, CHANNEL_APPOINTMENT);

  // Sichtbare Zusatzfelder: Wert aus dem Formular, Bezeichnung und Pflicht aus
  // der Konfiguration.
  const visibleFields = visibleConfig
    .filter((config) => !config.system_key)
    .map((config) => {
      const current = dynamicFields.find((f) => isSameField(f, config));
      return {
        ...(current || { ...config, value: "", isValid: true }),
        label: config.label,
        requiredHere: isRequiredFor(config, CHANNEL_APPOINTMENT),
      };
    });
  const [showValidation, setShowValidation] = useState(false);
  // Auswahl und Aufklappen sind getrennt: die Checkbox entscheidet, wer eine
  // Einladung bekommt, das Chevron öffnet die Details. Ausgewählte Teilnehmer
  // starten offen, weil dort Pflichtangaben liegen können.
  const [isExpanded, setIsExpanded] = useState(!!props.initiallyChecked);
  // Rückmeldung zum erneuten Versand: "sending" | "success" | "error" | null
  const [resendState, setResendState] = useState(null);
  const isResending = resendState === "sending";

  const initialTagSignature = (props.initialTagLabels || []).join("|");
  useEffect(() => {
    setSelectedTagLabels(props.initialTagLabels || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTagSignature]);

  // Signatur der Felddefinition: Titel, Typ und Pflicht. Ändert sich davon
  // nichts, bleiben eingegebene Werte und Prüfergebnisse bestehen.
  const fieldsSignature = props.dynamicFields
    .map((f) => `${f.title}:${f.field_type}:${f.type || f.info_type}`)
    .join("|");
  useEffect(() => {
    setDynamicFields(
      props.dynamicFields.map((field) => ({
        ...field,
        value: field.inserted_value || "",
        isValid: true,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsSignature]);

  // Standortwechsel: Tags und Türberechtigungen gehören zum alten Standort und
  // werden verworfen — gleiche Regel wie im Web. Beim ersten Feststehen des
  // Standorts passiert nichts.
  // Standardsprache übernehmen, solange nichts gewählt ist.
  const fallbackLanguage = defaultLanguage(languageOptions);
  useEffect(() => {
    if (!language && fallbackLanguage) setLanguage(fallbackLanguage);
  }, [fallbackLanguage, language]);

  // Stand der Karte nach oben spiegeln. Der Elternteil hält ihn und legt ihn
  // beim Neuaufbau der Liste wieder über die frischen Daten — sonst wäre alles
  // Eingetippte weg, sobald Outlook die Teilnehmer meldet oder eine
  // Serverantwort eintrifft. Gemeldet wird der Inhalt, nicht die Objekte,
  // deshalb hängt der Effekt an einer Signatur.
  const draftSignature = JSON.stringify([
    isChecked,
    name,
    company,
    language,
    selectedTagLabels,
    doorPermissions,
    dynamicFields.map((f) => [f.title, f.value]),
  ]);
  const onDraftChange = props.onDraftChange;
  useEffect(() => {
    if (!onDraftChange || !email) return;
    onDraftChange({
      email,
      isChecked,
      name,
      company,
      language,
      tagLabels: selectedTagLabels,
      doorPermissions,
      dynamicFields,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSignature, email]);

  const previousLocation = useRef(null);
  useEffect(() => {
    if (previousLocation.current === props.locationId) return;
    if (props.locationId == null) return;
    const isInitial = previousLocation.current == null;
    previousLocation.current = props.locationId;
    if (isInitial) return;
    setSelectedTagLabels([]);
    setDoorPermissions([]);
  }, [props.locationId]);

  // Über das Feld (ID bzw. Titel), nicht über den Index: gerendert wird die
  // gefilterte Liste (Kanal und Tag-Bindung), gespeichert wird in der vollen —
  // ein Index aus der einen zeigt in der anderen auf ein anderes Feld.
  const handleInputChange = (target, value) => {
    setDynamicFields((prevFields) =>
      prevFields.map((field) =>
        isSameField(field, target)
          ? { ...field, value, isValid: true, inserted_value: value }
          : field,
      ),
    );
  };

  const triggerValidation = () => {
    if (!isChecked) {
      return { isValid: true, missingFields: [] };
    }

    let isValid = true;
    let missingFields = [];

    const nameConfig = visibleConfig.find((f) => f.system_key === SYSTEM_NAME);
    if (!name.trim()) {
      isValid = false;
      missingFields.push(nameConfig?.label || "Name");
    }
    if (!email.trim()) {
      isValid = false;
      missingFields.push("Email");
    }
    if (companyRequired && !company.trim()) {
      isValid = false;
      missingFields.push(companyConfig.label);
    }
    if (isTagSelectionMissing(tagField, tagOptions, selectedTagLabels)) {
      isValid = false;
      missingFields.push(tagField.label);
    }
    if (durationIssue) {
      isValid = false;
      missingFields.push(
        `Termindauer (max. ${durationIssue.max} Tage für ${tagField.label})`,
      );
    }

    const updatedFields = dynamicFields.map((field) => {
      // Nur was in dieser Maske sichtbar ist, kann Pflicht sein — und ob es
      // Pflicht ist, sagt die Konfiguration samt Tag-Regeln, nicht der am
      // Besucher gespeicherte Stand.
      const shown = visibleFields.find((v) => isSameField(v, field));
      if (shown?.requiredHere) {
        if (field.field_type === "checkbox" || field.field_type === "toggle") {
          if (!isCheckedValue(field.value)) {
            isValid = false;
            missingFields.push(shown.label);
            return { ...field, isValid: false };
          }
        } else if (!String(field.value ?? "").trim()) {
          isValid = false;
          missingFields.push(shown.label);
          return { ...field, isValid: false };
        }
        return { ...field, isValid: true };
      }
      return field;
    });

    setDynamicFields(updatedFields);
    console.log("Validation completed in Visitor component:", isValid, missingFields);

    setShowValidation(true);
    if (!isValid) setIsExpanded(true);
    return { isValid, missingFields };
  };

  const getVisitorData = () => ({
    isChecked,
    name,
    email,
    company,
    dynamicFields,
    tagLabels: selectedTagLabels,
    doorPermissions,
    language,
  });

  useImperativeHandle(ref, () => ({
    triggerValidation,
    getVisitorData,
  }));

  const handleResendInvite = () => {
    if (!props.onResendInvite || isResending) return;
    setResendState("sending");
    props.onResendInvite({
      visitorId: props.visitorId,
      onDone: (ok) => setResendState(ok ? "success" : "error"),
    });
  };

  const handleCheckboxChange = () => {
    const newChecked = !isChecked;
    setIsChecked(newChecked);
    if (props.onCheckedChange) {
      props.onCheckedChange({ email, isChecked: newChecked });
    }
  };

  const renderField = (field) => {
    const { field_type, isValid } = field;
    // Pflicht und Bezeichnung kommen aus der Konfiguration (Kanal
    // Terminerstellung, Tag-Regeln) — ein Feld, das nur der Kiosk verlangt, ist
    // in dieser Maske optional.
    const isRequired = field.requiredHere;
    const label = field.label || field.title;
    const key = field.field_id || field.title;
    const value = field.value ?? "";
    const onValue = (next) => handleInputChange(field, next);
    // Pflichtfelder tragen wie das Tag-Feld ein `*` — bei Feldern ohne eigene
    // Beschriftung steht es im Platzhalter.
    const placeholder = isRequired ? `${label} *` : label;

    const labelClassName = isValid ? "" : "text-danger font-semibold";

    switch (field_type) {
      case "checkbox":
        return (
          <div key={key} className="flex items-center cursor-pointer my-2">
            <Checkbox
              label={placeholder}
              checked={isCheckedValue(value)}
              onChange={(next) => onValue(next ? "on" : "off")}
              className={labelClassName}
              disabled={props.isReadOnly}
            />
          </div>
        );
      case "toggle":
        return (
          <div key={key} className="flex items-center cursor-pointer my-2">
            <Toggle
              label={placeholder}
              checked={isCheckedValue(value)}
              onChange={(next) => onValue(next ? "on" : "off")}
              disabled={props.isReadOnly}
            />
          </div>
        );
      case "text":
        return (
          <Input
            key={key}
            placeholder={placeholder}
            type="text"
            value={value}
            onChange={(e) => onValue(e.target.value)}
            required={isRequired}
            className={isValid ? "" : "border-danger focus:border-danger"}
            disabled={props.isReadOnly}
          />
        );
      case "tel":
        return (
          <Input
            key={key}
            placeholder={placeholder}
            type="text"
            value={value}
            onChange={(e) => onValue(e.target.value)}
            required={isRequired}
            className={isValid ? "" : "border-danger focus:border-danger"}
            disabled={props.isReadOnly}
          />
        );
      case "number":
        return (
          <Input
            key={key}
            placeholder={placeholder}
            type="number"
            value={value}
            onChange={(e) => onValue(e.target.value)}
            required={isRequired}
            className={isValid ? "" : "border-danger focus:border-danger"}
            disabled={props.isReadOnly}
          />
        );
      case "dropdown":
        return (
          <Dropdown
            key={key}
            options={convertDropDownValues(field.accepted_values)}
            value={value}
            onChange={onValue}
            required={isRequired}
            placeholder={placeholder}
            className={isValid ? "" : "border-danger focus:border-danger"}
            disabled={props.isReadOnly}
          />
        );
      default:
        return null;
    }
  };

  const isNameSameAsEmail = name === email;
  const nameError = showValidation && (!name || isNameSameAsEmail);
  const companyError = showValidation && companyRequired && !company.trim();
  // Fehlt irgendwo eine Pflichtangabe? Die Karte wird dann rot umrandet, damit
  // man sie auch im zugeklappten Zustand findet.
  const hasMissingInput =
    showValidation &&
    (nameError ||
      companyError ||
      !email.trim() ||
      isTagSelectionMissing(tagField, tagOptions, selectedTagLabels) ||
      !!durationIssue ||
      visibleFields.some((field) => field.isValid === false));

  return (
    <div className="grid space-y-3 mt-4">
      <div
        className={cn(
          "border rounded-card bg-white transition-colors",
          isChecked ? "border-primary" : "border-gray",
        )}
      >
        <div className="flex items-center gap-[1rem] p-[1.2rem]">
          <UiCheckbox
            checked={isChecked}
            onChange={props.isReadOnly ? undefined : handleCheckboxChange}
            className="shrink-0"
          />
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="flex flex-1 min-w-0 flex-col items-start bg-transparent border-none p-0 text-left cursor-pointer font-[inherit]"
          >
            {name && !isNameSameAsEmail && (
              <span className="text-[1.3rem] font-semibold text-foreground truncate max-w-full">
                {name}
              </span>
            )}
            <span
              className={cn(
                "text-[1.2rem] truncate max-w-full",
                email ? "text-dim" : "text-danger",
              )}
            >
              {email || "E-Mail Adresse fehlt"}
            </span>
          </button>

          {/* Symbole immer an derselben Stelle, damit die Karten in der Liste
              gleich aussehen. */}
          <div className="flex items-center gap-[0.2rem] shrink-0">
            <LanguageSwitch
              value={language}
              options={languageOptions}
              onChange={setLanguage}
              disabled={props.isReadOnly}
            />
            {props.canResendInvite && (
              <IconButton
                size="sm"
                label={
                  resendState === "success"
                    ? "Einladung wurde erneut versendet"
                    : resendState === "error"
                      ? "Versand fehlgeschlagen - nochmals versuchen"
                      : "Einladung erneut senden"
                }
                disabled={isResending}
                onClick={handleResendInvite}
                className={cn(
                  resendState === "success" && "text-[#16a34a]",
                  resendState === "error" && "text-danger",
                )}
              >
                {resendState === "success" ? <CheckmarkCircleRegular /> : <MailRegular />}
              </IconButton>
            )}
            <IconButton
              size="sm"
              label={isExpanded ? "Details ausblenden" : "Details anzeigen"}
              onClick={() => setIsExpanded((v) => !v)}
            >
              <ChevronDown20Regular
                className={cn("transition-transform", isExpanded && "rotate-180")}
              />
            </IconButton>
          </div>
        </div>
        {isExpanded && (
          <div className="px-[1.2rem] pb-[1.2rem] space-y-[0.8rem]">
            {/* Name, Firma und Zusatzfelder in der eingestellten Reihenfolge */}
            {visibleConfig.map((config) => {
              const placeholder = isRequiredFor(config, CHANNEL_APPOINTMENT)
                ? `${config.label} *`
                : config.label;
              if (config.system_key === SYSTEM_NAME) {
                return (
                  <VisitorInput
                    key="name"
                    placeholder={placeholder}
                    value={isNameSameAsEmail ? "" : name}
                    error={nameError}
                    onChange={(event) => setName(event.target.value)}
                    disabled={props.isReadOnly}
                  />
                );
              }
              if (config.system_key === SYSTEM_COMPANY) {
                return (
                  <VisitorInput
                    key="company"
                    placeholder={placeholder}
                    value={company}
                    error={companyError}
                    onChange={(event) => setCompany(event.target.value)}
                    disabled={props.isReadOnly}
                  />
                );
              }
              const field = visibleFields.find((f) => isSameField(f, config));
              return field ? renderField(field) : null;
            })}
            <div className="mt-[1.2rem]">
              <VisitorTagField
                value={selectedTagLabels}
                options={tagOptions}
                locationId={props.locationId}
                disabled={props.isReadOnly}
                error={
                  showValidation &&
                  isTagSelectionMissing(tagField, tagOptions, selectedTagLabels)
                }
                onChange={handleTagChange}
              />
              {durationIssue && (
                <p className="text-[1.15rem] text-danger m-0 mt-[0.4rem]">
                  Der Termin dauert {durationIssue.days} Tage - für diese Auswahl
                  sind höchstens {durationIssue.max} Tage erlaubt. Bitte den
                  Termin in Outlook kürzen.
                </p>
              )}
            </div>
            {showDoorPermissions && (
              <DoorPermissionFields
                value={doorPermissions}
                optionsByType={optionsByType}
                disabled={props.isReadOnly}
                onChange={setDoorPermissions}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default forwardRef(Visitor);
